import React, { useCallback, useMemo } from 'react'
import dayjs from 'dayjs'
import { Line } from 'react-chartjs-2'
import {
  CategoryScale,
  Chart as ChartJS,
  ChartArea,
  Filler,
  Interaction,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  ChartData,
} from 'chart.js'
// @ts-ignore
import { CrosshairPlugin, Interpolate } from 'chartjs-plugin-crosshair'
import { ContextData } from '@floating-ui/react-dom-interactions'
import { DateRange } from '../../lib/types/DateRange'
import { uniq } from 'lodash'
import { isPDFGenerationModeEnabled } from '../../lib/utils/isPDFGenerationModeEnabled'

ChartJS.defaults.font.family = 'Spartan'
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
  CrosshairPlugin,
)
// @ts-ignore
Interaction.modes.interpolate = Interpolate
const disableAnimations = isPDFGenerationModeEnabled()
  ? {
      animation: false,
    }
  : {}
const options = {
  ...(disableAnimations as any),
  responsive: true,
  stacked: false,
  elements: {
    line: {
      tension: 0.5,
    },
    point: {
      radius: 0,
    },
  },
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
    },
    crosshair: {
      line: {
        color: '#121124', // crosshair line color
        width: 1, // crosshair line width
      },
      sync: {
        enabled: false,
      },
      zoom: {
        enabled: false,
      },
      snap: {
        enabled: false,
      },
    },
    tooltip: {
      mode: 'index' as const,
      intersect: false,
    },
  },
}

const getBlueGradient = (ctx: ContextData, chartArea: ChartArea): ContextData => {
  // gradient from top to bottom
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
  // #4776E6 with 0.9 opacity
  gradient.addColorStop(0, 'rgba(71, 118, 230, 0.9)')
  // #8E54E9 with 0.5 opacity
  gradient.addColorStop(0.8, 'rgba(142, 84, 233, 0.5)')
  // #837CD1 with 0.5 opacity
  gradient.addColorStop(1, 'rgba(131, 124, 209, 0.5)')
  return gradient
}

const getRedGradient = (ctx: ContextData, chartArea: ChartArea): ContextData => {
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
  //
  // linear-gradient(180deg, #F2709C 14.76%, rgba(255, 148, 114, 0.49) 100.01%)
  gradient.addColorStop(0.47, '#F2709C')
  gradient.addColorStop(1, 'rgba(255, 148, 114, 0.49)')
  return gradient
}

const getGreenToLemonGreenGradient = (
  ctx: ContextData,
  chartArea: ChartArea,
): ContextData => {
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
  // #279661 0%, #8ECB5F 100%
  // linear-gradient(180deg, #8ECB5F 0%, rgba(142, 203, 95, 0) 100%)
  gradient.addColorStop(0, '#8ECB5F')
  gradient.addColorStop(1, 'rgba(142, 203, 95, 0)')
  return gradient
}

const getGreenToCyanGradient = (ctx: ContextData, chartArea: ChartArea): ContextData => {
  // linear-gradient(180deg, #70EC8C 14.76%, rgba(6, 210, 238, 0.5) 100.01%)
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
  gradient.addColorStop(0.5, '#70EC8C')
  gradient.addColorStop(1, 'rgba(6, 210, 238, 0.5)')
  return gradient
}

const getBlueToCyanGraident = (ctx: ContextData, chartArea: ChartArea): ContextData => {
  // linear-gradient(180deg, rgba(111, 138, 236, 0.5) 57.64%, rgba(0, 194, 255, 0.25) 100%)
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
  gradient.addColorStop(0.5, 'rgba(111, 138, 236, 0.5)')
  gradient.addColorStop(1, 'rgba(0, 194, 255, 0.25)')
  return gradient
}

const gradients = {
  'blue-to-purple': getBlueGradient,
  'red-to-orange': getRedGradient,
  'green-to-lemon-green': getGreenToLemonGreenGradient,
  'green-to-cyan': getGreenToCyanGradient,
  'blue-to-cyan': getBlueToCyanGraident,
}

const MultiLineChart = ({
  data,
  gradient,
  yKey,
  groupKey,
  labelKey,
  date_range,
  isLoading = false,
  isPreviousData = false,
  borderColor,
  borderWidth = 0,
  fill = true,
  showBackground = true,
  customOptions,
  xKey,
  customLabel,
}: {
  gradient: (keyof typeof gradients)[]
  yKey: string
  data: Array<any>
  groupKey: string
  labelKey: string
  date_range?: DateRange
  isLoading?: boolean
  isPreviousData?: boolean
  borderColor?: string
  borderWidth?: number
  fill?: boolean
  customOptions?: Record<string, unknown>
  showBackground?: boolean
  xKey?: string
  customLabel?: (label: string) => string
}): JSX.Element => {
  const minScale = useMemo(() => {
    const min = Math.min(...data.map((d) => d[yKey]))
    const magnitude = Math.pow(10, Math.floor(Math.log10(Math.abs(min))))
    const roundedMin = min > 0 ? Math.floor(min / magnitude) * magnitude : 0
    return roundedMin
  }, [data, yKey])

  const maxScale = useMemo(() => {
    const max = Math.max(...data.map((d) => d[yKey]))
    const magnitude = Math.pow(10, Math.floor(Math.log10(Math.abs(max))))
    const roundedMax = Math.ceil(max / magnitude) * magnitude
    return roundedMax
  }, [data, yKey])

  const labels = useMemo(() => {
    if (date_range) {
      const start = dayjs(date_range.dateRange.startDate)
      const end = dayjs(date_range.dateRange.endDate)
      let curr = start.clone()
      const arr: string[] = []
      while (curr <= end) {
        arr.push(curr.format('MM-DD'))
        curr = dayjs(curr).add(1, 'days')
      }
      return arr
    } else if (xKey) {
      return uniq(data?.map((d) => d[xKey])).sort((a, b) => a - b)
    }
  }, [date_range, data, xKey])

  const ds = useMemo(() => {
    return Array.from(new Set(data.map((d) => d[groupKey])))
  }, [data, groupKey])

  const data_for_key = useCallback(
    (key: string) => {
      if (xKey) {
        const entries = labels?.map((l) => {
          const entry = data.find((x) => x[xKey] === l && x[groupKey] === key) ?? {
            [yKey]: 0,
          }
          return entry[yKey]
        })
        return entries
      }
      const entries = labels?.map((l) => {
        const entry = data.find(
          (x) => dayjs(x.date).format('MM-DD') === l && x[groupKey] === key,
        ) ?? { [yKey]: 0 }
        return entry[yKey]
      })
      return entries
    },
    [data, labels, groupKey, yKey, xKey],
  )

  const chartData = {
    labels: labels?.map((label) => (customLabel ? customLabel(label) : label)),
    datasets: ds.map((d, i) => ({
      yAxisID: data.find((x) => x[groupKey] === d)?.axis ?? 'y',
      label: data.find((x) => x[groupKey] === d)[labelKey],
      data: data_for_key(d),
      borderColor: (context: ContextData) => {
        const chart = context.chart
        const { ctx, chartArea } = chart
        if (!chartArea) return
        if (!gradient[i]) return
        return gradients[gradient[i]](ctx, chartArea)
      },
      fill,
      borderWidth,
      datalabels: {
        display: false,
      },
      backgroundColor: (context: ContextData) => {
        const chart = context.chart
        const { ctx, chartArea } = chart
        if (!chartArea) return
        if (!gradient[i]) return
        if (!showBackground) return 'rgba(0,0,0,0)'
        return gradients[gradient[i]](ctx, chartArea)
      },
    })),
  }
  if (isLoading) {
    return (
      <div className={'w-full h-full flex items-center justify-center'}>Loading...</div>
    )
  }

  if (data.length === 0) {
    return (
      <div
        className={
          'w-full h-full flex items-center justify-center text-web-gray opacity-50'
        }>
        <Line
          options={{
            ...options,
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                mode: 'index' as const,
                intersect: false,
              },
            },
          }}
          data={{
            labels: labels,
            datasets: [
              {
                label: '',
                data: [],
                borderColor: borderColor,
                fill: true,
                borderWidth: 0,
                datalabels: {
                  display: false,
                },
              },
            ],
          }}
        />
      </div>
    )
  }

  return (
    <div className={'relative h-full w-full'}>
      {isPreviousData && (
        <div className={'absolute top-0 left-0 w-full h-full bg-white opacity-50'} />
      )}
      <Line
        options={{
          scales: { y: { min: minScale, max: maxScale } },
          ...options,
          ...customOptions,
        }}
        data={chartData as ChartData<'line', number[]>}
      />
    </div>
  )
}

export default MultiLineChart
